import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { PhotoUpload, EvidenceThumb } from "../components/Evidence";
import { RecordRepairButton } from "../components/RecordRepair";
import WorkLogPanel from "../components/WorkLogPanel";
import { toast } from "sonner";
import { Plus, Trash, Wrench, CheckCircle, Warning, Money, Package, Truck, MagnifyingGlass, PencilSimple, ArrowUUpLeft, LinkSimple, X, Printer, ArrowsClockwise } from "@phosphor-icons/react";
import { PriorityBadge, PrioritySelect } from "../components/Priority";
import { fmtDate, fmtDateTime } from "../lib/date";

const OUTCOMES = [
  { v: "resolved", label: "Resolved — sign off fault" },
  { v: "partial", label: "Partially resolved — further work required" },
  { v: "not_resolved", label: "Not resolved" },
  { v: "not_worked", label: "Not worked on" },
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.v, o.label]));

const RECORD_STATE = {
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
  reopened: { label: "Reopened", cls: "bg-amber-100 text-amber-700" },
  corrected: { label: "Corrected", cls: "bg-blue-100 text-blue-700" },
  void: { label: "Void", cls: "bg-red-100 text-red-700" },
};

const JOB_STATUS_LABEL = {
  draft: "Draft", open: "Draft", in_progress: "In Progress", completed: "Completed",
  reopened: "Reopened", void: "Void", cancelled: "Void",
  awaiting_parts: "Paused — Awaiting Parts", awaiting_approval: "Paused — Awaiting Approval",
  awaiting_external: "Paused — Awaiting External Work", paused_other: "Paused — Other",
};

const emptyItem = () => ({
  work_type_id: "", description: "", work_date: new Date().toISOString().slice(0, 10), technician: "",
  mileage: "", hours: "", parts: [], labour: "", labour_cost: "", parts_cost: "", supplier: "",
  photos: [], linked_fault_id: "", is_safety_critical: false, testing: "", notes: "",
});

function PartRow({ it, tag, tagCls, onClick, active }) {
  return (
    <button data-testid={`job-stock-result-${it.id}`} onClick={onClick}
      className={`block w-full text-left text-sm px-2 py-2 rounded mt-1 border ${active ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
      <div className="flex items-center gap-2">
        {it.photo_url && <img src={it.photo_url} alt="" className="w-8 h-8 rounded object-cover" />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800">{it.name} {tag && <span className={`text-[10px] font-bold uppercase px-1 py-0.5 rounded ${tagCls}`}>{tag}</span>}</div>
          <div className="font-mono text-xs text-slate-500">
            <b>{it.available}</b> avail{it.part_number ? ` · ${it.part_number}` : ""}{it.manufacturer_name ? ` · ${it.manufacturer_name}` : ""} · {it.location_label || "no location"}
          </div>
        </div>
      </div>
    </button>
  );
}

function StockUsePanel({ jobId, jobAssetId, onUsed }) {
  const [ranked, setRanked] = useState({ reserved: [], confirmed: [], possible: [] });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("stock"); // stock | new
  const [np, setNp] = useState({ name: "", quantity: "", location_id: "", part_number: "", manufacturer_id: "", notes: "", confirmFit: true });
  const [refs, setRefs] = useState({ locations: [], mans: [] });
  const busyRef = useRef(false);
  const loadRanked = useCallback(() => {
    if (jobAssetId) api.get(`/maintenance/stock/for-asset/${jobAssetId}`).then((r) => setRanked(r.data)).catch(() => {});
  }, [jobAssetId]);
  useEffect(() => { loadRanked(); }, [loadRanked]);
  useEffect(() => { Promise.all([api.get("/config/stock-locations"), api.get("/config/manufacturers")]).then(([l, m]) => setRefs({ locations: l.data, mans: m.data })).catch(() => {}); }, []);
  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/maintenance/stock/items?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const otherAlloc = (sel?.reservations || []).filter((a) => a.kind === "asset" && a.asset_id !== jobAssetId);
  const use = async () => {
    if (busyRef.current || !sel) return;
    if (otherAlloc.length > 0) {
      const who = otherAlloc.map((a) => `${a.quantity} for ${a.label}`).join(", ");
      if (!window.confirm(`Heads up: some of this stock is reserved for another vehicle (${who}). Use it here anyway?`)) return;
    }
    busyRef.current = true; setBusy(true);
    try {
      const r = await api.post(`/maintenance/stock/items/${sel.id}/use`, { maintenance_job_id: jobId, quantity: Number(qty) });
      toast.success(`Used ${qty} × ${sel.name} · ${r.data.available} left`);
      setSel(null); setQ(""); setResults([]); setQty(1); loadRanked(); onUsed();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const addNewPart = async () => {
    if (busyRef.current) return;
    if (!np.name.trim()) { toast.error("Part name required"); return; }
    busyRef.current = true; setBusy(true);
    try {
      const r = await api.post("/maintenance/stock/items", { name: np.name, quantity: np.quantity || 0, location_id: np.location_id || null, part_number: np.part_number || null, manufacturer_id: np.manufacturer_id || null, notes: np.notes || null });
      if (np.confirmFit && jobAssetId) await api.post(`/maintenance/stock/items/${r.data.id}/fitment`, { target_type: "asset", target_id: jobAssetId, status: "fits" });
      toast.success("New part added"); setSel(r.data); setMode("stock");
      setNp({ name: "", quantity: "", location_id: "", part_number: "", manufacturer_id: "", notes: "", confirmFit: true }); loadRanked();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { busyRef.current = false; setBusy(false); }
  };
  return (
    <div className="bg-white border-2 border-slate-300 rounded-lg p-4" data-testid="job-stock-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="font-display text-lg font-black text-slate-900 flex items-center gap-2"><Package size={18} weight="bold" />Add Part Used</h2>
        <div className="flex gap-2">
          <Button data-testid="job-part-mode-stock" onClick={() => setMode("stock")} variant={mode === "stock" ? "default" : "outline"} className="h-8 text-xs font-semibold">Select From Stock</Button>
          <Button data-testid="job-part-mode-new" onClick={() => setMode("new")} variant={mode === "new" ? "default" : "outline"} className="h-8 text-xs font-semibold"><Plus size={14} className="mr-1" />Add New Part</Button>
        </div>
      </div>

      {mode === "new" ? (
        <div className="flex flex-col gap-2" data-testid="job-part-new-form">
          <Input data-testid="np-name" placeholder="Part name *" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className="h-9" />
          <div className="flex gap-2">
            <Input data-testid="np-qty" type="number" placeholder="Qty in stock" value={np.quantity} onChange={(e) => setNp({ ...np, quantity: e.target.value })} className="h-9 w-28" />
            <select data-testid="np-loc" className="h-9 border-2 border-slate-300 rounded px-2 text-sm flex-1" value={np.location_id} onChange={(e) => setNp({ ...np, location_id: e.target.value })}><option value="">Location…</option>{refs.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
          </div>
          <div className="flex gap-2">
            <Input data-testid="np-partnum" placeholder="Part number" value={np.part_number} onChange={(e) => setNp({ ...np, part_number: e.target.value })} className="h-9 flex-1" />
            <select data-testid="np-manu" className="h-9 border-2 border-slate-300 rounded px-2 text-sm flex-1" value={np.manufacturer_id} onChange={(e) => setNp({ ...np, manufacturer_id: e.target.value })}><option value="">Manufacturer…</option>{refs.mans.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
          </div>
          <Input data-testid="np-notes" placeholder="Notes" value={np.notes} onChange={(e) => setNp({ ...np, notes: e.target.value })} className="h-9" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="np-confirmfit" checked={np.confirmFit} onChange={(e) => setNp({ ...np, confirmFit: e.target.checked })} />Mark as Confirmed Fit for this asset</label>
          <Button data-testid="np-save" disabled={busy} onClick={addNewPart} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold disabled:opacity-60">Add part</Button>
        </div>
      ) : (
        <>
          {ranked.reserved?.length > 0 && <div><div className="text-xs font-bold uppercase text-indigo-700 mt-1">Reserved for this asset</div>{ranked.reserved.map((it) => <PartRow key={"r" + it.id} it={it} tag="Reserved" tagCls="bg-indigo-100 text-indigo-700" active={sel?.id === it.id} onClick={() => setSel(it)} />)}</div>}
          {ranked.confirmed?.length > 0 && <div><div className="text-xs font-bold uppercase text-emerald-700 mt-2">Confirmed fit</div>{ranked.confirmed.map((it) => <PartRow key={"c" + it.id} it={it} tag="Fits" tagCls="bg-emerald-100 text-emerald-700" active={sel?.id === it.id} onClick={() => setSel(it)} />)}</div>}
          {ranked.possible?.length > 0 && <div><div className="text-xs font-bold uppercase text-amber-700 mt-2">Might fit — verify before fitting</div>{ranked.possible.map((it) => <PartRow key={"p" + it.id} it={it} tag="Might fit" tagCls="bg-amber-100 text-amber-700" active={sel?.id === it.id} onClick={() => setSel(it)} />)}</div>}
          <div className="text-xs font-bold uppercase text-slate-500 mt-3 mb-1">Search all stock</div>
          <div className="relative"><MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" /><Input data-testid="job-stock-search" placeholder="Search any part…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8" /></div>
          {results.map((it) => <PartRow key={"s" + it.id} it={it} active={sel?.id === it.id} onClick={() => setSel(it)} />)}
        </>
      )}

      {mode === "stock" && sel && (
        <div className="mt-3 border-t border-slate-200 pt-2">
          {otherAlloc.length > 0 && <div className="text-xs text-indigo-700 font-semibold mb-1" data-testid="job-stock-alloc-warning"><Truck size={12} weight="bold" className="inline mr-1" />Some reserved for {otherAlloc.map((a) => a.label).join(", ")} — still usable here</div>}
          <div className="flex items-center gap-2">
            <span className="text-sm flex-1">{sel.name} ({sel.available} available)</span>
            <Input data-testid="job-stock-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 w-20" />
            <Button data-testid="job-stock-use" disabled={busy} onClick={use} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold disabled:opacity-60">{busy ? "Working…" : "Use"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const OUTCOME_BADGE = {
  resolved: "bg-emerald-100 text-emerald-700",
  partial: "bg-amber-100 text-amber-700",
  not_resolved: "bg-red-100 text-red-700",
  not_worked: "bg-slate-200 text-slate-600",
};

function LinkedIssueCard({ lf, canEdit, onRemove }) {
  return (
    <div data-testid={`linked-issue-${lf.fault_id}`} className={`border-2 rounded-lg p-3 ${lf.final_signoff_at ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`linked-issue-ref-${lf.fault_id}`}>#{lf.ref}</span>
            <PriorityBadge priority={lf.priority} testid={`linked-issue-priority-${lf.fault_id}`} />
            <span className="font-body font-semibold text-slate-900">{lf.title}</span>
            {lf.resolution_status && <span data-testid={`linked-issue-outcome-${lf.fault_id}`} className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${OUTCOME_BADGE[lf.resolution_status] || "bg-slate-100 text-slate-600"}`}>{OUTCOME_LABEL[lf.resolution_status] || lf.resolution_status}</span>}
          </div>
          {lf.description && lf.description !== lf.title && <p className="font-body text-sm text-slate-700 mt-1 whitespace-pre-wrap">{lf.description}</p>}
          <p className="font-mono text-xs text-slate-500 mt-1">
            {lf.severity_name ? `${lf.severity_name} · ` : ""}{lf.final_signoff_at ? "Closed" : (lf.status_name || "Open")}
            {lf.reporter_name ? ` · by ${lf.reporter_name}` : ""}{lf.reported_at ? ` · ${fmtDate(lf.reported_at)}` : ""}
          </p>
          {lf.resolution_notes && <p className="text-xs text-slate-600 mt-1 italic">Outcome note: {lf.resolution_notes}</p>}
          {lf.photos?.length > 0 && <div className="flex gap-2 mt-2">{lf.photos.map((ph) => <EvidenceThumb key={ph.id} fileId={ph.id} />)}</div>}
        </div>
        {canEdit && (
          <button data-testid={`unlink-issue-${lf.fault_id}`} onClick={() => onRemove(lf.fault_id)} title="Remove from this job (does not delete the issue)" className="text-red-600 shrink-0"><X size={18} weight="bold" /></button>
        )}
      </div>
    </div>
  );
}

export default function MaintenanceJobPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm, profile } = useAuth();
  const canWrite = hasPerm("maintenance", "supervisor", "administrator");
  const [job, setJob] = useState(null);
  const [asset, setAsset] = useState(null);
  const [denied, setDenied] = useState(false);
  const [workTypes, setWorkTypes] = useState([]);
  const [faults, setFaults] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [wi, setWi] = useState(emptyItem());
  const [completeOpen, setCompleteOpen] = useState(false);
  const [comp, setComp] = useState({ completion_date: new Date().toISOString().slice(0, 10), completion_mileage: "", completion_hours: "" });
  const [resolutions, setResolutions] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [jform, setJform] = useState({ title: "", description: "", notes: "", priority: "medium", assigned_to_id: "", assigned_team_id: "", due_date: "" });
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const canReverse = hasPerm("asset_manager", "supervisor", "administrator");
  const [reverseSel, setReverseSel] = useState(null);
  const [reversePrev, setReversePrev] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [rtsInfo, setRtsInfo] = useState(null);
  const [rtsForm, setRtsForm] = useState({ test_result: "", comments: "" });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkable, setLinkable] = useState(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [photoUrls, setPhotoUrls] = useState({});
  const [printReq, setPrintReq] = useState(0);
  const fetchedRef = useRef(new Set());
  const canReopen = hasPerm("supervisor", "administrator");
  const canAdmin = hasPerm("administrator");
  const [mtypes, setMtypes] = useState([]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [aform, setAform] = useState({});
  const [adminReason, setAdminReason] = useState("");
  const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

  const load = useCallback(() => {
    api.get(`/maintenance/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, [id]);
  useEffect(() => {
    load();
    api.get("/config/work-types").then((r) => setWorkTypes(r.data)).catch(() => {});
    if (canWrite) {
      api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
      api.get("/config/teams").then((r) => setTeams(r.data)).catch(() => {});
      api.get("/config/maintenance-types").then((r) => setMtypes(r.data)).catch(() => {});
    }
  }, [load, canWrite]);
  useEffect(() => {
    if (!job?.asset_id) return;
    api.get(`/faults?asset_id=${job.asset_id}`).then((r) => setFaults(r.data)).catch(() => {});
    api.get(`/assets/${job.asset_id}`).then((r) => setAsset(r.data)).catch(() => {});
  }, [job?.asset_id]);
  useEffect(() => {
    if (!job) return;
    const ids = [];
    (job.linked_faults || []).forEach((lf) => (lf.photos || []).forEach((p) => p?.id && ids.push(p.id)));
    (job.work_items || []).forEach((w) => (w.photos || []).forEach((p) => p?.id && ids.push(p.id)));
    const missing = ids.filter((x) => !fetchedRef.current.has(x));
    if (!missing.length) return;
    missing.forEach((x) => fetchedRef.current.add(x));
    Promise.all(missing.map((fid) => api.get(`/files/${fid}/url`).then((r) => [fid, r.data.url]).catch(() => [fid, null])))
      .then((pairs) => setPhotoUrls((prev) => { const n = { ...prev }; pairs.forEach(([fid, url]) => { if (url) n[fid] = url; }); return n; }));
  }, [job]);

  useEffect(() => {
    if (!printReq) return;
    document.body.classList.add("printing-job-pack");
    const cleanup = () => { document.body.classList.remove("printing-job-pack"); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    window.print();
    const t = setTimeout(cleanup, 1500);
    return () => clearTimeout(t);
  }, [printReq]);

  if (denied) return <p className="text-slate-600 font-body">You don't have permission to view maintenance jobs.</p>;
  if (!job) return <p className="text-slate-500">Loading job…</p>;

  const isCompleted = job.status === "completed";
  const linkedFaults = job.linked_faults || [];

  const setF = (k, v) => setWi((p) => ({ ...p, [k]: v }));
  const addPart = () => setWi((p) => ({ ...p, parts: [...p.parts, { description: "", part_number: "", quantity: 1, position: "" }] }));
  const setPart = (i, k, v) => setWi((p) => { const parts = [...p.parts]; parts[i] = { ...parts[i], [k]: v }; return { ...p, parts }; });
  const rmPart = (i) => setWi((p) => ({ ...p, parts: p.parts.filter((_, x) => x !== i) }));

  const saveItem = async () => {
    try {
      const payload = { ...wi };
      ["mileage", "hours", "labour_cost", "parts_cost"].forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
      payload.photos = wi.photos.map((p) => ({ id: p.id, name: p.name }));
      await api.post(`/maintenance/jobs/${id}/work-items`, payload);
      toast.success("Work item added"); setAddOpen(false); setWi(emptyItem()); load();
    } catch (e) { toast.error("Failed"); }
  };
  const delItem = async (wid) => {
    try { await api.delete(`/maintenance/work-items/${wid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openLink = () => {
    setLinkable(null); setLinkOpen(true);
    api.get(`/maintenance/jobs/${id}/linkable-faults`).then((r) => setLinkable(r.data)).catch(() => setLinkable([]));
  };
  const linkFault = async (fid) => {
    try {
      await api.post(`/maintenance/jobs/${id}/faults`, { fault_id: fid });
      toast.success("Issue linked to job card"); setLinkOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unlinkFault = async (fid) => {
    if (!window.confirm("Remove this issue from the job card?\nThe issue itself will NOT be deleted or closed.")) return;
    try { await api.delete(`/maintenance/jobs/${id}/faults/${fid}`); toast.success("Issue removed from job card"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) { toast.error("A reason is required to reopen a job"); return; }
    try {
      const r = await api.post(`/maintenance/jobs/${id}/reopen`, { reason: reopenReason });
      await load();
      setReopenOpen(false); setReopenReason("");
      toast.success(`Job reopened for correction (revision ${r.data.revision})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const printPack = () => setPrintReq((n) => n + 1);
  const printPhotos = (photos) => (photos?.length ? (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
      {photos.map((p) => (photoUrls[p.id] ? <img key={p.id} src={photoUrls[p.id]} alt="evidence" style={{ maxHeight: 120, border: "1px solid #ccc", borderRadius: 4 }} /> : null))}
    </div>
  ) : null);
  const printFaultRef = (fid) => { const lf = (job.linked_faults || []).find((l) => l.fault_id === fid); return lf ? `#${lf.ref}` : String(fid).slice(0, 6); };
  const readWlDraft = () => {
    try {
      const d = JSON.parse(localStorage.getItem(`wl-draft-${id}`) || "{}");
      return (d.work_done || d.findings || d.parts_note || d.measurements || d.notes) ? d : null;
    } catch { return null; }
  };
  const myName = profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() : "";

  const openAdminEdit = () => {
    setAform({
      title: job.title || "", description: job.description || "", notes: job.notes || "",
      priority: job.priority || "medium", status: job.status || "open",
      maintenance_type_id: job.maintenance_type_id || "", assigned_to_id: job.assigned_to_id || "",
      assigned_team_id: job.assigned_team_id || "", due_date: job.due_date || "",
      started_at: toLocalInput(job.started_at), completed_at: toLocalInput(job.completed_at),
      job_number: job.job_number || "", work_completed: job.work_completed || "",
      labour: job.labour || "", cost: job.cost ?? "", test_result: job.test_result || "",
    });
    setAdminReason(""); setAdminOpen(true);
  };
  const setA = (k, v) => setAform((p) => ({ ...p, [k]: v }));
  const saveAdmin = async () => {
    if (!adminReason.trim()) { toast.error("An edit reason is required"); return; }
    const payload = { edit_reason: adminReason };
    const cur = job;
    ["title", "description", "notes", "priority", "status", "work_completed", "labour", "test_result", "job_number"].forEach((k) => {
      const nv = aform[k] ?? ""; if ((cur[k] ?? "") !== nv) payload[k] = nv || null;
    });
    ["maintenance_type_id", "assigned_to_id", "assigned_team_id"].forEach((k) => {
      const nv = aform[k] || null; if ((cur[k] || null) !== nv) payload[k] = nv;
    });
    const nc = aform.cost === "" || aform.cost == null ? null : Number(aform.cost);
    const oc = cur.cost == null ? null : Number(cur.cost);
    if (oc !== nc) payload.cost = nc;
    const nd = aform.due_date || null; if ((cur.due_date || null) !== nd) payload.due_date = nd;
    const sIso = aform.started_at ? new Date(aform.started_at).toISOString() : null;
    if ((cur.started_at ? new Date(cur.started_at).getTime() : null) !== (sIso ? new Date(sIso).getTime() : null)) payload.started_at = sIso;
    const cIso = aform.completed_at ? new Date(aform.completed_at).toISOString() : null;
    if ((cur.completed_at ? new Date(cur.completed_at).getTime() : null) !== (cIso ? new Date(cIso).getTime() : null)) payload.completed_at = cIso;
    try {
      const r = await api.put(`/maintenance/jobs/${id}/admin`, payload);
      await load();
      setAdminOpen(false);
      toast.success(`Job card updated (admin) — ${r.data.change_count || 0} change(s) audited`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openComplete = () => {
    const init = {};
    linkedFaults.forEach((lf) => { init[lf.fault_id] = { status: "", notes: "" }; });
    setResolutions(init);
    setComp({ completion_date: new Date().toISOString().slice(0, 10), completion_mileage: "", completion_hours: "" });
    setCompleteOpen(true);
  };
  const setResolution = (fid, k, v) => setResolutions((p) => ({ ...p, [fid]: { ...p[fid], [k]: v } }));
  const complete = async () => {
    try {
      const payload = { ...comp };
      ["completion_mileage", "completion_hours"].forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
      payload.fault_resolutions = Object.entries(resolutions)
        .filter(([, r]) => r.status)
        .map(([fault_id, r]) => ({ fault_id, status: r.status, notes: r.notes || null }));
      const r = await api.post(`/maintenance/jobs/${id}/complete`, payload);
      await load();
      const nd = r.data.next_due_recalculated;
      toast.success(nd ? `Completed — next service due ${nd.next_due_date ? fmtDate(nd.next_due_date) : (nd.next_due_value || "recalculated")}` : "Job completed");
      setCompleteOpen(false);
      const rts = r.data.rts;
      if (rts?.required && rts.can_return) { setRtsForm({ test_result: "", comments: "" }); setRtsInfo(rts); }
      else if (rts?.required && !rts.can_return) { toast.warning("Work complete — asset set to Awaiting Return to Service. An authorised approver has been notified."); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doReturnToService = async () => {
    if (!rtsForm.test_result.trim()) { toast.error("A test result is required"); return; }
    try {
      const r = await api.post(`/assets/${job.asset_id}/return-to-service`, {
        test_result: rtsForm.test_result, comments: rtsForm.comments, maintenance_job_id: id, target_status_code: "available",
      });
      const d = r.data || {};
      if (d.blockers && d.blockers.length) toast.warning(`Asset NOT returned to service — ${d.blockers.join("; ")}`);
      else if (d.returned_to_service === false) toast.warning("Return to service is pending approval.");
      else toast.success("Asset returned to service");
      setRtsInfo(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openReverse = async (w) => {
    setReverseSel(w); setReverseReason(""); setReversePrev(null);
    try { const r = await api.get(`/maintenance/work-items/${w.id}/reverse-preview`); setReversePrev(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot reverse"); setReverseSel(null); }
  };
  const confirmReverse = async () => {
    if (!reverseReason.trim()) { toast.error("A reason is required"); return; }
    try {
      const r = await api.post(`/maintenance/work-items/${reverseSel.id}/reverse`, { reason: reverseReason });
      let msg = "Work entry reversed";
      if (r.data.returned) msg += ` · ${r.data.returned.quantity} × ${r.data.returned.item} returned to stock`;
      if (r.data.asset_effect === "awaiting_rts") toast.warning("Asset set to Awaiting Return to Service — fresh RTS review required.");
      toast.success(msg); setReverseSel(null); setReversePrev(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEditJob = () => {
    setJform({ title: job.title || "", description: job.description || "", notes: job.notes || "", priority: job.priority || "medium",
               assigned_to_id: job.assigned_to_id || "", assigned_team_id: job.assigned_team_id || "", due_date: job.due_date || "" });
    setEditOpen(true);
  };
  const saveJob = async () => {
    try {
      await api.put(`/maintenance/jobs/${id}`, {
        title: jform.title, description: jform.description, notes: jform.notes, priority: jform.priority,
        assigned_to_id: jform.assigned_to_id || null, assigned_team_id: jform.assigned_team_id || null, due_date: jform.due_date || null,
      });
      toast.success("Job updated"); setEditOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const money = (v) => (v == null ? null : `£${Number(v).toFixed(2)}`);
  const totalCost = (job.work_items || []).filter((w) => !w.voided).reduce((s, w) => s + Number(w.total_cost || 0), 0);

  return (
    <div className="flex flex-col gap-5 max-w-3xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {job.job_number && <span data-testid="job-number" className="font-mono text-xs font-bold uppercase px-2 py-0.5 rounded bg-slate-900 text-white">{job.job_number}</span>}
            <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">{job.title || "Maintenance job"}</h1>
            {!isCompleted && job.asset_id && <RecordRepairButton assetId={job.asset_id} jobId={job.id} testid="job-record-repair" onDone={load} className="h-8 text-xs" />}
            {!isCompleted && <PriorityBadge priority={job.priority} testid="job-priority-badge" />}
            {job.record_state && <span data-testid="job-record-state" className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${RECORD_STATE[job.record_state]?.cls || "bg-slate-100 text-slate-600"}`}>{RECORD_STATE[job.record_state]?.label || job.record_state}</span>}
          </div>
          <p className="font-mono text-sm text-slate-500 mt-1">Status: {job.status}{job.due_date ? ` · due ${fmtDate(job.due_date)}` : ""}{job.assignee_name ? ` · ${job.assignee_name}` : ""}</p>
          {job.reopened_at && <p className="font-body text-xs text-amber-700 mt-1" data-testid="job-reopen-note">Reopened (rev {job.revision}){job.reopened_by_name ? ` by ${job.reopened_by_name}` : ""} — {job.reopen_reason}</p>}
          {job.description && <p className="font-body text-sm text-slate-700 mt-2 whitespace-pre-wrap" data-testid="job-description">{job.description}</p>}
          {job.notes && <p className="font-body text-xs text-slate-500 mt-1 italic whitespace-pre-wrap" data-testid="job-notes">Notes: {job.notes}</p>}
          {job.asset_id && <Link to={`/assets/${job.asset_id}`} className="text-sm text-blue-600 font-semibold">View asset →</Link>}
        </div>
        <div className="flex flex-col gap-2 items-end print:hidden">
          <Button data-testid="print-job" variant="outline" onClick={printPack} className="font-semibold h-9"><Printer size={16} weight="bold" className="mr-1" />Print / PDF</Button>
          {canWrite && !isCompleted && (
            <Button data-testid="edit-job" variant="outline" onClick={openEditJob} className="font-semibold h-9"><PencilSimple size={16} weight="bold" className="mr-1" />Edit job</Button>
          )}
          {canWrite && !isCompleted && (
            <Button data-testid="complete-job" onClick={openComplete} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase">
              <CheckCircle size={18} weight="bold" className="mr-1" />Complete Job
            </Button>
          )}
          {isCompleted && canReopen && (
            <Button data-testid="reopen-job" onClick={() => setReopenOpen(true)} className="bg-amber-600 hover:bg-amber-700 font-bold uppercase h-9">
              <ArrowsClockwise size={16} weight="bold" className="mr-1" />Reopen
            </Button>
          )}
          {canAdmin && (
            <Button data-testid="admin-edit-job" variant="outline" onClick={openAdminEdit} className="font-semibold h-9 border-purple-400 text-purple-700">
              <PencilSimple size={16} weight="bold" className="mr-1" />Admin Edit
            </Button>
          )}
        </div>
      </div>

      {/* Related Issues / Repairs (many-to-many, asset-scoped) */}
      <div className="bg-white border-2 border-slate-300 rounded-lg" data-testid="linked-issues-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-200 bg-slate-50 flex-wrap gap-2">
          <h2 className="font-display text-xl font-black text-slate-900 flex items-center gap-2"><Warning size={20} weight="bold" />Related Issues{linkedFaults.length > 0 ? ` (${linkedFaults.length})` : ""}</h2>
          {canWrite && !isCompleted && job.asset_id && (
            <Button data-testid="add-related-issue" onClick={openLink} className="bg-slate-900 hover:bg-slate-800 font-semibold h-9"><LinkSimple size={16} weight="bold" className="mr-1" />Add Related Issue</Button>
          )}
        </div>
        <div className="p-3 flex flex-col gap-3">
          {isCompleted && <p className="text-xs text-slate-500">This job is completed — its linked issues and recorded outcomes are locked as a historical record.</p>}
          {linkedFaults.length ? linkedFaults.map((lf) => (
            <LinkedIssueCard key={lf.fault_id} lf={lf} canEdit={canWrite && !isCompleted} onRemove={unlinkFault} />
          )) : <p className="text-slate-400 text-sm p-2">No issues linked yet. Use "Add Related Issue" to attach open defects, reported faults or inspection findings for this asset.</p>}
        </div>
      </div>

      <WorkLogPanel job={job} linkedFaults={linkedFaults} isCompleted={isCompleted} onChange={load} />

      {/* Work Carried Out */}
      <div className="bg-white border-2 border-slate-300 rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-200 bg-slate-50">
          <h2 className="font-display text-xl font-black text-slate-900 flex items-center gap-2"><Wrench size={20} weight="bold" />Work Carried Out</h2>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-bold text-slate-600 flex items-center gap-1"><Money size={16} weight="bold" />{money(totalCost)}</span>
            {canWrite && <Button data-testid="add-work-item" onClick={() => { setWi({ ...emptyItem(), linked_fault_id: linkedFaults[0]?.fault_id || "" }); setAddOpen(true); }} className="bg-slate-900 hover:bg-slate-800 font-semibold h-9"><Plus size={16} weight="bold" className="mr-1" />Add</Button>}
          </div>
        </div>
        <div className="p-3 flex flex-col gap-3">
          {job.work_items?.length ? job.work_items.map((w) => (
            <div key={w.id} data-testid={`work-item-${w.id}`} className={`border-2 rounded-lg p-3 ${w.voided ? "border-red-200 bg-red-50/40 opacity-70" : w.is_safety_critical ? "border-red-300" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className={`font-body font-semibold text-slate-900 ${w.voided ? "line-through" : ""}`}>{w.work_type_name || "Work"}</span>
                  {w.voided && <span data-testid={`work-item-voided-${w.id}`} className="ml-2 text-xs font-bold uppercase text-red-600">Reversed</span>}
                  {w.is_safety_critical && !w.voided && <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold uppercase text-red-600"><Warning size={12} weight="fill" />Safety-critical</span>}
                  <p className="font-body text-sm text-slate-700 mt-1">{w.description}</p>
                  <p className="font-mono text-xs text-slate-500 mt-1">
                    {fmtDate(w.work_date) || "—"}{w.technician ? ` · ${w.technician}` : (w.performed_by_name ? ` · ${w.performed_by_name}` : "")}
                    {w.mileage != null ? ` · ${w.mileage} mi` : ""}{w.hours != null ? ` · ${w.hours} h` : ""}{w.total_cost != null ? ` · ${money(w.total_cost)}` : ""}
                  </p>
                  {w.voided && w.void_reason && <p className="text-xs text-red-600 mt-1">Reversal reason: {w.void_reason}{w.voided_by_name ? ` — ${w.voided_by_name}` : ""}</p>}
                  {w.parts?.length > 0 && (
                    <div className="mt-2 text-xs font-mono text-slate-600">
                      {w.parts.map((p, i) => <div key={i}>• {p.description} {p.part_number ? `(${p.part_number})` : ""} × {p.quantity}{p.position ? ` @ ${p.position}` : ""}</div>)}
                    </div>
                  )}
                  {w.testing && <p className="text-xs text-slate-600 mt-1">Testing: {w.testing}</p>}
                  {w.photos?.length > 0 && <div className="flex gap-2 mt-2">{w.photos.map((ph) => <EvidenceThumb key={ph.id} fileId={ph.id} />)}</div>}
                </div>
                {!w.voided && (
                  <div className="flex items-center gap-2 shrink-0">
                    {canReverse && <button data-testid={`reverse-work-item-${w.id}`} onClick={() => openReverse(w)} className="text-amber-600 inline-flex items-center gap-1 text-xs font-semibold"><ArrowUUpLeft size={16} weight="bold" />Reverse</button>}
                    {canWrite && !isCompleted && <button data-testid={`del-work-item-${w.id}`} onClick={() => delItem(w.id)} className="text-red-600"><Trash size={18} weight="bold" /></button>}
                  </div>
                )}
              </div>
            </div>
          )) : <p className="text-slate-400 text-sm p-2">No work recorded yet. Add the work carried out.</p>}
        </div>
      </div>

      {/* Parts from stock */}
      {canWrite && !isCompleted && <StockUsePanel jobId={id} jobAssetId={job.asset_id} onUsed={load} />}

      {/* Add work item */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Add Work Item</DialogTitle></DialogHeader>
          <select data-testid="wi-type" className="h-10 border-2 border-slate-300 rounded px-2" value={wi.work_type_id} onChange={(e) => setF("work_type_id", e.target.value)}>
            <option value="">Work type…</option>{workTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Textarea data-testid="wi-description" placeholder="Description of work carried out" value={wi.description} onChange={(e) => setF("description", e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Date</label><Input data-testid="wi-date" type="date" value={wi.work_date} onChange={(e) => setF("work_date", e.target.value)} /></div>
            <Input data-testid="wi-technician" placeholder="Technician / company" value={wi.technician} onChange={(e) => setF("technician", e.target.value)} />
            <Input data-testid="wi-mileage" type="number" placeholder="Mileage" value={wi.mileage} onChange={(e) => setF("mileage", e.target.value)} />
            <Input data-testid="wi-hours" type="number" placeholder="Hours" value={wi.hours} onChange={(e) => setF("hours", e.target.value)} />
          </div>

          {/* Parts repeater */}
          <div className="border-2 border-dashed border-slate-300 rounded p-2">
            <div className="flex items-center justify-between mb-1"><span className="font-body text-sm font-semibold text-slate-700">Parts</span>
              <Button data-testid="wi-add-part" onClick={addPart} className="h-8 bg-slate-700 font-semibold text-xs"><Plus size={14} weight="bold" className="mr-1" />Part</Button></div>
            {wi.parts.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1 items-center">
                <Input data-testid={`part-desc-${i}`} className="col-span-4 h-9" placeholder="Part" value={p.description} onChange={(e) => setPart(i, "description", e.target.value)} />
                <Input data-testid={`part-num-${i}`} className="col-span-3 h-9" placeholder="Part no." value={p.part_number} onChange={(e) => setPart(i, "part_number", e.target.value)} />
                <Input data-testid={`part-qty-${i}`} className="col-span-2 h-9" type="number" placeholder="Qty" value={p.quantity} onChange={(e) => setPart(i, "quantity", e.target.value)} />
                <Input data-testid={`part-pos-${i}`} className="col-span-2 h-9" placeholder="Pos." value={p.position} onChange={(e) => setPart(i, "position", e.target.value)} />
                <button onClick={() => rmPart(i)} className="col-span-1 text-red-600"><Trash size={16} weight="bold" /></button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="wi-parts-cost" type="number" placeholder="Parts cost £" value={wi.parts_cost} onChange={(e) => setF("parts_cost", e.target.value)} />
            <Input data-testid="wi-labour-cost" type="number" placeholder="Labour cost £" value={wi.labour_cost} onChange={(e) => setF("labour_cost", e.target.value)} />
            <Input data-testid="wi-labour" placeholder="Labour (hrs / notes)" value={wi.labour} onChange={(e) => setF("labour", e.target.value)} />
            <Input data-testid="wi-supplier" placeholder="Supplier" value={wi.supplier} onChange={(e) => setF("supplier", e.target.value)} />
          </div>
          {(linkedFaults.length > 0 || faults.length > 0) && (
            <select data-testid="wi-fault" className="h-10 border-2 border-slate-300 rounded px-2" value={wi.linked_fault_id} onChange={(e) => setF("linked_fault_id", e.target.value)}>
              <option value="">Link a related issue (optional)…</option>
              {linkedFaults.map((lf) => <option key={lf.fault_id} value={lf.fault_id}>#{lf.ref} · {lf.title?.slice(0, 50)}</option>)}
            </select>
          )}
          <Textarea data-testid="wi-testing" placeholder="Testing / results" value={wi.testing} onChange={(e) => setF("testing", e.target.value)} rows={2} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="wi-safety" checked={wi.is_safety_critical} onChange={(e) => setF("is_safety_critical", e.target.checked)} />Safety-critical work</label>
          <div className="flex items-center gap-3 flex-wrap">
            <PhotoUpload category="maintenance" label="Photo / Document" onUploaded={(f) => setF("photos", [...wi.photos, f])} />
            <div className="flex gap-2">{wi.photos.map((p) => <EvidenceThumb key={p.id} fileId={p.id} />)}</div>
          </div>
          <Button data-testid="wi-save" onClick={saveItem} disabled={!wi.work_type_id} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save Work Item</Button>
        </DialogContent>
      </Dialog>

      {/* Add related issue (asset-scoped selector) */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Add Related Issue</DialogTitle>
            <DialogDescription>Only issues for this asset{job.asset_number ? ` (#${job.asset_number})` : ""} are shown. Pick any open defect, reported fault or inspection finding to include in this job.</DialogDescription>
          </DialogHeader>
          {linkable === null ? <p className="text-slate-400 text-sm">Loading issues…</p> : linkable.length === 0 ? (
            <p className="text-slate-400 text-sm" data-testid="linkable-empty">No other issues found for this asset.</p>
          ) : (
            <div className="flex flex-col gap-2" data-testid="linkable-list">
              {linkable.map((f) => (
                <button key={f.id} data-testid={`linkable-issue-${f.id}`} onClick={() => linkFault(f.id)}
                  className="text-left border-2 border-slate-200 hover:border-slate-900 rounded-lg p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{f.ref}</span>
                    <PriorityBadge priority={f.priority} testid={`linkable-priority-${f.id}`} />
                    <span className="font-body font-semibold text-slate-900">{f.title}</span>
                    {!f.open && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Closed</span>}
                  </div>
                  <p className="font-mono text-xs text-slate-500 mt-1">{f.severity_name ? `${f.severity_name} · ` : ""}{f.status_name || (f.open ? "Open" : "Closed")}{f.reported_at ? ` · ${fmtDate(f.reported_at)}` : ""}</p>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit job info */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Edit Job Information</DialogTitle></DialogHeader>
          <label className="text-xs font-semibold text-slate-600">Title</label>
          <Input data-testid="edit-job-title" value={jform.title} onChange={(e) => setJform({ ...jform, title: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Work required / description</label>
          <Textarea data-testid="edit-job-description" value={jform.description} onChange={(e) => setJform({ ...jform, description: e.target.value })} rows={3} />
          <label className="text-xs font-semibold text-slate-600">Maintenance notes</label>
          <Textarea data-testid="edit-job-notes" value={jform.notes} onChange={(e) => setJform({ ...jform, notes: e.target.value })} rows={2} />
          <label className="text-xs font-semibold text-slate-600">Priority</label>
          <PrioritySelect testid="edit-job-priority" value={jform.priority} onChange={(v) => setJform({ ...jform, priority: v })} className="w-full" />
          <label className="text-xs font-semibold text-slate-600">Assigned engineer</label>
          <select data-testid="edit-job-assignee" className="h-10 border-2 border-slate-300 rounded px-2" value={jform.assigned_to_id} onChange={(e) => setJform({ ...jform, assigned_to_id: e.target.value })}>
            <option value="">Unassigned…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
          <label className="text-xs font-semibold text-slate-600">Assigned team</label>
          <select data-testid="edit-job-team" className="h-10 border-2 border-slate-300 rounded px-2" value={jform.assigned_team_id} onChange={(e) => setJform({ ...jform, assigned_team_id: e.target.value })}>
            <option value="">No team…</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label className="text-xs font-semibold text-slate-600">Target date</label>
          <Input data-testid="edit-job-due" type="date" value={jform.due_date || ""} onChange={(e) => setJform({ ...jform, due_date: e.target.value })} />
          <Button data-testid="edit-job-save" onClick={saveJob} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save Changes</Button>
        </DialogContent>
      </Dialog>

      {/* Complete job */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Complete Job</DialogTitle></DialogHeader>
          <p className="font-body text-sm text-slate-600">If this job is linked to a service schedule, the next service due will be recalculated from the actual completion reading/date.</p>
          <div><label className="text-xs font-semibold text-slate-600">Completion date</label><Input data-testid="comp-date" type="date" value={comp.completion_date} onChange={(e) => setComp({ ...comp, completion_date: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="comp-mileage" type="number" placeholder="Completion mileage" value={comp.completion_mileage} onChange={(e) => setComp({ ...comp, completion_mileage: e.target.value })} />
            <Input data-testid="comp-hours" type="number" placeholder="Completion hours" value={comp.completion_hours} onChange={(e) => setComp({ ...comp, completion_hours: e.target.value })} />
          </div>
          {linkedFaults.length > 0 && (
            <div className="border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-3" data-testid="complete-resolutions">
              <p className="font-body text-sm font-semibold text-slate-800">Outcome for each linked issue</p>
              <p className="text-xs text-slate-500 -mt-2">Completing the job does not close issues automatically. Only issues marked <b>Resolved</b> are signed off.</p>
              {linkedFaults.map((lf) => (
                <div key={lf.fault_id} className="flex flex-col gap-1" data-testid={`resolution-${lf.fault_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{lf.ref}</span>
                    <span className="font-body text-sm font-semibold text-slate-800">{lf.title}</span>
                  </div>
                  <select data-testid={`resolution-status-${lf.fault_id}`} className="h-9 border-2 border-slate-300 rounded px-2 text-sm"
                    value={resolutions[lf.fault_id]?.status || ""} onChange={(e) => setResolution(lf.fault_id, "status", e.target.value)}>
                    <option value="">Leave unchanged…</option>
                    {OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                  {resolutions[lf.fault_id]?.status && (
                    <Input data-testid={`resolution-notes-${lf.fault_id}`} placeholder="Outcome note (optional)" value={resolutions[lf.fault_id]?.notes || ""} onChange={(e) => setResolution(lf.fault_id, "notes", e.target.value)} className="h-9" />
                  )}
                </div>
              ))}
            </div>
          )}
          <Button data-testid="comp-confirm" onClick={complete} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase">Confirm Completion</Button>
        </DialogContent>
      </Dialog>

      {/* Return to Service after completion (user HAS authority) */}
      <Dialog open={!!rtsInfo} onOpenChange={(o) => !o && setRtsInfo(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Work complete — Return asset to service?</DialogTitle>
            <DialogDescription>This asset is currently {rtsInfo?.asset_status_name || "not operational"}. You are authorised to return it to service.</DialogDescription>
          </DialogHeader>
          <label className="text-xs font-semibold text-slate-600">Test result / evidence *</label>
          <Textarea data-testid="rts-test-result" value={rtsForm.test_result} onChange={(e) => setRtsForm({ ...rtsForm, test_result: e.target.value })} placeholder="e.g. Road tested, all systems normal" />
          <label className="text-xs font-semibold text-slate-600">Comment / signature</label>
          <Input data-testid="rts-comments" value={rtsForm.comments} onChange={(e) => setRtsForm({ ...rtsForm, comments: e.target.value })} />
          <div className="flex gap-2">
            <Button data-testid="rts-return" onClick={doReturnToService} className="bg-emerald-600 hover:bg-emerald-700 font-bold flex-1">Return to Service</Button>
            <Button data-testid="rts-keep" variant="outline" onClick={() => setRtsInfo(null)} className="font-semibold flex-1">Keep Out of Service</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reverse work entry */}
      <Dialog open={!!reverseSel} onOpenChange={(o) => !o && (setReverseSel(null), setReversePrev(null))}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Reverse this work entry?</DialogTitle>
            <DialogDescription>The record is voided (never erased) and the audit trail is preserved.</DialogDescription>
          </DialogHeader>
          {reversePrev ? (
            <div className="text-sm text-slate-700 flex flex-col gap-1" data-testid="reverse-preview">
              <p className="font-semibold">{reversePrev.work_type || "Work"} — {reversePrev.description}</p>
              {reversePrev.stock_returns?.map((s, i) => <p key={i} className="text-emerald-700">• Return {s.quantity} × {s.name} to stock</p>)}
              {reversePrev.cost > 0 && <p className="text-slate-700">• Reverse {money(reversePrev.cost)} recorded cost</p>}
              {reversePrev.parts?.length > 0 && <p className="text-slate-700">• Remove associated parts usage</p>}
              <p className="text-slate-700">• Recalculate job totals{reversePrev.will_reopen_job ? " and reopen the completed job" : ""}</p>
              {reversePrev.rts_warning && <p data-testid="reverse-rts-warning" className="mt-1 p-2 rounded bg-red-50 border-2 border-red-200 text-red-700 font-semibold text-xs">⚠ This asset was already RETURNED TO SERVICE using this work. Reversing it will set the asset to Awaiting Return to Service and require a fresh RTS review.</p>}
            </div>
          ) : <p className="text-slate-400 text-sm">Loading preview…</p>}
          <label className="text-xs font-semibold text-slate-600">Reason for reversal *</label>
          <Textarea data-testid="reverse-reason" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="Why is this being reversed?" />
          <Button data-testid="reverse-confirm" onClick={confirmReverse} className="bg-amber-600 hover:bg-amber-700 font-bold uppercase">Confirm Reversal</Button>
        </DialogContent>
      </Dialog>

      {/* Reopen job for correction */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Reopen this completed job?</DialogTitle>
            <DialogDescription>Reopening unlocks the linked issues and their outcomes so they can be corrected. This is audited (revision {(job.revision || 0) + 1}) — history is never edited silently. Changing an issue away from "Resolved" will reopen the underlying fault only if this job card is still the current reason it is closed.</DialogDescription>
          </DialogHeader>
          <label className="text-xs font-semibold text-slate-600">Reason for reopening *</label>
          <Textarea data-testid="reopen-reason" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Why is this job being reopened?" />
          <Button data-testid="reopen-confirm" onClick={reopen} className="bg-amber-600 hover:bg-amber-700 font-bold uppercase">Confirm Reopen</Button>
        </DialogContent>
      </Dialog>

      {/* Admin full-field edit (audited) */}
      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent className="bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Admin Edit Job Card</DialogTitle>
            <DialogDescription>Admins may edit any job-card field. Every change is field-level audited (who/when/old/new + reason); the previous version stays recoverable. Affected asset status is recalculated — compliance/safety holds are never silently cleared.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><label className="text-xs font-semibold text-slate-600">Job number</label><Input data-testid="admin-job-number" value={aform.job_number || ""} onChange={(e) => setA("job_number", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-slate-600">Title</label><Input data-testid="admin-title" value={aform.title || ""} onChange={(e) => setA("title", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Status</label>
              <select data-testid="admin-status" className="h-10 w-full border-2 border-slate-300 rounded px-2" value={aform.status || ""} onChange={(e) => setA("status", e.target.value)}>
                {["open", "in_progress", "awaiting_parts", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold text-slate-600">Priority</label>
              <select data-testid="admin-priority" className="h-10 w-full border-2 border-slate-300 rounded px-2" value={aform.priority || ""} onChange={(e) => setA("priority", e.target.value)}>
                {["urgent", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold text-slate-600">Maintenance type</label>
              <select data-testid="admin-mtype" className="h-10 w-full border-2 border-slate-300 rounded px-2" value={aform.maintenance_type_id || ""} onChange={(e) => setA("maintenance_type_id", e.target.value)}>
                <option value="">—</option>{mtypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold text-slate-600">Assigned engineer</label>
              <select data-testid="admin-assignee" className="h-10 w-full border-2 border-slate-300 rounded px-2" value={aform.assigned_to_id || ""} onChange={(e) => setA("assigned_to_id", e.target.value)}>
                <option value="">Unassigned</option>{people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold text-slate-600">Team</label>
              <select data-testid="admin-team" className="h-10 w-full border-2 border-slate-300 rounded px-2" value={aform.assigned_team_id || ""} onChange={(e) => setA("assigned_team_id", e.target.value)}>
                <option value="">No team</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold text-slate-600">Target date</label><Input data-testid="admin-due" type="date" value={aform.due_date || ""} onChange={(e) => setA("due_date", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Work started</label><Input data-testid="admin-started" type="datetime-local" value={aform.started_at || ""} onChange={(e) => setA("started_at", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Completed</label><Input data-testid="admin-completed" type="datetime-local" value={aform.completed_at || ""} onChange={(e) => setA("completed_at", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Cost (£)</label><Input data-testid="admin-cost" type="number" value={aform.cost ?? ""} onChange={(e) => setA("cost", e.target.value)} /></div>
          </div>
          <label className="text-xs font-semibold text-slate-600">Description</label>
          <Textarea data-testid="admin-description" value={aform.description || ""} onChange={(e) => setA("description", e.target.value)} rows={2} />
          <label className="text-xs font-semibold text-slate-600">Work completed / diagnosis</label>
          <Textarea data-testid="admin-work-completed" value={aform.work_completed || ""} onChange={(e) => setA("work_completed", e.target.value)} rows={2} />
          <label className="text-xs font-semibold text-slate-600">Labour</label>
          <Input data-testid="admin-labour" value={aform.labour || ""} onChange={(e) => setA("labour", e.target.value)} />
          <label className="text-xs font-semibold text-slate-600">Test result</label>
          <Input data-testid="admin-test-result" value={aform.test_result || ""} onChange={(e) => setA("test_result", e.target.value)} />
          <label className="text-xs font-semibold text-slate-600">Notes</label>
          <Textarea data-testid="admin-notes" value={aform.notes || ""} onChange={(e) => setA("notes", e.target.value)} rows={2} />
          <label className="text-xs font-semibold text-red-700">Reason for admin edit (required, audited) *</label>
          <Textarea data-testid="admin-edit-reason" value={adminReason} onChange={(e) => setAdminReason(e.target.value)} rows={2} placeholder="Why is this job card being edited?" />
          <Button data-testid="admin-edit-save" onClick={saveAdmin} className="bg-purple-700 hover:bg-purple-800 font-bold uppercase">Save Admin Edit</Button>
        </DialogContent>
      </Dialog>

      {/* A4 print / Save-as-PDF handover pack (hidden on screen) */}
      <div id="job-print-pack" className="hidden text-black" data-testid="job-print-pack">
        <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #000", paddingBottom: 8, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>Job Card {job.job_number || ""}</div>
              <div style={{ fontSize: 13 }}>{job.title}</div>
            </div>
            <div style={{ textAlign: "right", fontSize: 12 }}>
              <div style={{ fontWeight: 800, textTransform: "uppercase" }}>{RECORD_STATE[job.record_state]?.label || job.status}</div>
              {(job.revision || 0) > 0 && <div>Revision {job.revision}</div>}
              <div>Printed {new Date().toLocaleString("en-GB")}</div>
            </div>
          </div>
          <table style={{ width: "100%", fontSize: 12, marginBottom: 12 }}><tbody>
            <tr><td style={{ fontWeight: 700, width: 140, padding: "2px 0", verticalAlign: "top" }}>Asset</td><td>{job.asset_number ? `#${job.asset_number} · ` : ""}{job.asset_name || "—"}{job.asset_registration ? ` · ${job.asset_registration}` : ""}</td></tr>
            <tr><td style={{ fontWeight: 700 }}>Status</td><td>{job.status}{job.priority ? ` · priority ${job.priority}` : ""}</td></tr>
            <tr><td style={{ fontWeight: 700 }}>Assigned</td><td>{job.assignee_name || "Unassigned"}</td></tr>
            {job.due_date && <tr><td style={{ fontWeight: 700 }}>Target date</td><td>{fmtDate(job.due_date)}</td></tr>}
            {job.completed_at && <tr><td style={{ fontWeight: 700 }}>Completed</td><td>{fmtDate(job.completed_at)}</td></tr>}
            {job.reopened_at && <tr><td style={{ fontWeight: 700 }}>Reopened</td><td>rev {job.revision}{job.reopened_by_name ? ` by ${job.reopened_by_name}` : ""} — {job.reopen_reason}</td></tr>}
          </tbody></table>
          {job.description && <div style={{ fontSize: 12, marginBottom: 12 }}><b>Work required:</b> {job.description}</div>}

          <div style={{ fontSize: 14, fontWeight: 800, borderBottom: "1px solid #000", marginBottom: 6 }}>Related Issues ({linkedFaults.length})</div>
          {linkedFaults.length ? linkedFaults.map((lf) => (
            <div key={lf.fault_id} style={{ borderBottom: "1px solid #ccc", padding: "6px 0", fontSize: 12, breakInside: "avoid" }}>
              <div><b>#{lf.ref}</b> — {lf.title} <span style={{ textTransform: "uppercase", fontSize: 10 }}>[{lf.priority || "priority n/a"}]</span>{lf.resolution_status ? ` — ${OUTCOME_LABEL[lf.resolution_status] || lf.resolution_status}` : ""}</div>
              <div style={{ color: "#444" }}>{lf.severity_name ? `${lf.severity_name} · ` : ""}{lf.final_signoff_at ? "Closed" : (lf.status_name || "Open")}{lf.reporter_name ? ` · reported by ${lf.reporter_name}` : ""}{lf.reported_at ? ` · ${fmtDate(lf.reported_at)}` : ""}</div>
              {lf.description && lf.description !== lf.title && <div>{lf.description}</div>}
              {lf.resolution_notes && <div><i>Outcome: {lf.resolution_notes}</i></div>}
              {printPhotos(lf.photos)}
            </div>
          )) : <div style={{ fontSize: 12, color: "#666" }}>No issues linked.</div>}

          <div style={{ fontSize: 14, fontWeight: 800, borderBottom: "1px solid #000", margin: "12px 0 6px" }}>Work Carried Out</div>
          {(job.work_items || []).filter((w) => !w.voided).length ? job.work_items.filter((w) => !w.voided).map((w) => (
            <div key={w.id} style={{ borderBottom: "1px solid #ccc", padding: "6px 0", fontSize: 12, breakInside: "avoid" }}>
              <div><b>{w.work_type_name || "Work"}</b> — {fmtDate(w.work_date)}{w.technician ? ` · ${w.technician}` : (w.performed_by_name ? ` · ${w.performed_by_name}` : "")}</div>
              <div>{w.description}</div>
              {(w.mileage != null || w.hours != null || w.total_cost != null) && <div style={{ color: "#444" }}>{w.mileage != null ? `${w.mileage} mi · ` : ""}{w.hours != null ? `${w.hours} h · ` : ""}{w.total_cost != null ? money(w.total_cost) : ""}</div>}
              {w.parts?.length > 0 && <div>{w.parts.map((p, i) => <div key={i}>• {p.description} {p.part_number ? `(${p.part_number})` : ""} × {p.quantity}</div>)}</div>}
              {w.testing && <div>Testing: {w.testing}</div>}
              {printPhotos(w.photos)}
            </div>
          )) : <div style={{ fontSize: 12, color: "#666" }}>No work recorded.</div>}

          {/* Technician Work Log — full chronological history, attributed per technician */}
          <div style={{ fontSize: 14, fontWeight: 800, borderBottom: "1px solid #000", margin: "12px 0 6px" }}>Technician Work Log</div>
          {(job.work_log || []).length ? (job.work_log || []).map((e) => (
            <div key={e.id} style={{ borderBottom: "1px solid #ccc", padding: "8px 0", fontSize: 12, breakInside: "avoid" }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                {e.technician_name || "—"}
                {e.entry_date ? ` — ${fmtDate(e.entry_date)}` : ""}
                {e.start_time ? ` ${e.start_time}` : ""}{e.finish_time ? `–${e.finish_time}` : ""}
                {e.entry_type === "correction" ? " · CORRECTION (audited)" : ""}
                {e.corrected ? " · [superseded by correction]" : ""}
              </div>
              {e.created_at && <div style={{ color: "#777", fontSize: 10 }}>Logged {fmtDateTime(e.created_at)}</div>}
              {e.correction_reason && <div style={{ color: "#444" }}><b>Reason:</b> {e.correction_reason}</div>}
              {e.work_done && <div style={{ whiteSpace: "pre-wrap" }}><b>Work carried out:</b> {e.work_done}</div>}
              {e.findings && <div><b>Findings:</b> {e.findings}</div>}
              {e.parts_note && <div><b>Parts:</b> {e.parts_note}</div>}
              {e.measurements && <div><b>Measurements:</b> {e.measurements}</div>}
              {e.duration_minutes != null && e.duration_minutes !== "" && <div><b>Time:</b> {e.duration_minutes} min</div>}
              {e.notes && <div><b>Notes:</b> {e.notes}</div>}
              {(e.linked_fault_ids || []).length > 0 && <div><b>Linked issue(s):</b> {e.linked_fault_ids.map(printFaultRef).join(", ")}</div>}
              {printPhotos(e.photos)}
            </div>
          )) : <div style={{ fontSize: 12, color: "#666" }}>No work log entries yet.</div>}
          {(() => {
            const d = readWlDraft();
            if (!d) return null;
            return (
              <div style={{ border: "2px dashed #b45309", background: "#fffbeb", padding: "8px", fontSize: 12, marginTop: 6, breakInside: "avoid" }}>
                <div style={{ fontWeight: 800, textTransform: "uppercase", color: "#b45309" }}>Unsaved draft entry — not yet logged</div>
                <div style={{ fontSize: 10, color: "#777" }}>{myName ? `${myName} · ` : ""}{new Date().toLocaleString("en-GB")}</div>
                {d.work_done && <div style={{ whiteSpace: "pre-wrap" }}><b>Work carried out:</b> {d.work_done}</div>}
                {d.findings && <div><b>Findings:</b> {d.findings}</div>}
                {d.parts_note && <div><b>Parts:</b> {d.parts_note}</div>}
                {d.measurements && <div><b>Measurements:</b> {d.measurements}</div>}
                {d.notes && <div><b>Notes:</b> {d.notes}</div>}
              </div>
            );
          })()}

          {/* Current status / handover — so the next technician can pick the job up */}
          <div style={{ fontSize: 14, fontWeight: 800, borderBottom: "1px solid #000", margin: "12px 0 6px" }}>Current Status / Handover</div>
          <table style={{ width: "100%", fontSize: 12, marginBottom: 12 }}><tbody>
            <tr><td style={{ fontWeight: 700, width: 180, padding: "2px 0", verticalAlign: "top" }}>Job status</td><td>{JOB_STATUS_LABEL[job.status] || job.status}</td></tr>
            {asset?.effective_status?.name && <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Asset use status</td><td>{asset.effective_status.name}{(asset.effective_status.reasons || []).length ? ` — ${asset.effective_status.reasons.join("; ")}` : ""}</td></tr>}
            {asset?.effective_status?.recorded_status === "awaiting_rts" && <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Return to Service</td><td>Required — awaiting authorised return to service</td></tr>}
            {asset?.restriction?.reason && <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Restriction / Do Not Use</td><td>{asset.restriction.reason}{asset.restriction.permitted_use ? ` · Permitted: ${asset.restriction.permitted_use}` : ""}</td></tr>}
            {job.parts_awaiting && <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Parts awaiting</td><td>{job.parts_awaiting}</td></tr>}
            {job.outstanding_work && <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Work outstanding</td><td>{job.outstanding_work}</td></tr>}
            {job.handover_note && <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Next action</td><td>{job.handover_note}</td></tr>}
            <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Assigned to</td><td>{job.assignee_name || "Unassigned"}</td></tr>
            {job.last_activity_by_name && <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>Last activity</td><td>{job.last_activity_by_name}{job.last_activity_at ? ` · ${fmtDateTime(job.last_activity_at)}` : ""}</td></tr>}
          </tbody></table>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, fontSize: 11 }}>
            <div style={{ borderTop: "1px solid #000", width: "45%", paddingTop: 4 }}>Engineer signature / date</div>
            <div style={{ borderTop: "1px solid #000", width: "45%", paddingTop: 4 }}>Supervisor signature / date</div>
          </div>
        </div>
      </div>

      {/* Post-action navigation */}
      <div className="flex gap-2 flex-wrap pt-2">
        {job.asset_id && <Button data-testid="nav-back-asset" variant="outline" onClick={() => nav(`/assets/${job.asset_id}`)} className="font-semibold">Back to Asset</Button>}
        <Button data-testid="nav-back-maintenance" variant="outline" onClick={() => nav("/maintenance")} className="font-semibold">Back to Maintenance</Button>
      </div>
    </div>
  );
}
